const { MAX_ARMS, MAX_PARTICIPANTS, MIN_ARMS } = require('../constants');

function toPositiveInteger(value, fieldName) {
  const parsed = Number(value);

  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error(`${fieldName} must be a positive integer`);
  }

  return parsed;
}

function validateCoreParams(params) {
  const n = toPositiveInteger(params.n, 'N');
  const arms = toPositiveInteger(params.arms, 'Arms');
  const seed = params.seed === undefined || params.seed === null ? '' : String(params.seed).trim();
  const armLabels = normalizeArmLabels(params.armLabels, arms);

  const errors = [];

  if (n < 1 || n > MAX_PARTICIPANTS) {
    errors.push(`N must be between 1 and ${MAX_PARTICIPANTS} patients`);
  }

  if (arms < MIN_ARMS || arms > MAX_ARMS) {
    errors.push(`Arms must be between ${MIN_ARMS} and ${MAX_ARMS}`);
  }

  if (!seed) {
    errors.push('Seed is required for reproducibility');
  }

  if (errors.length > 0) {
    throw new Error(errors.join('; '));
  }

  return {
    ...params,
    n,
    arms,
    seed,
    armLabels
  };
}

function normalizeArmLabels(inputLabels, arms) {
  const rawLabels = Array.isArray(inputLabels) ? inputLabels : [];
  const labels = Array.from({ length: arms }, (_, index) => {
    const fallback = `Arm ${index + 1}`;
    const label = rawLabels[index] === undefined || rawLabels[index] === null
      ? fallback
      : String(rawLabels[index]).trim();

    if (!label) {
      return fallback;
    }

    if (label.length > 60) {
      throw new Error(`Arm ${index + 1} label must be 60 characters or less`);
    }

    return label;
  });

  const normalizedNames = labels.map((label) => label.toLowerCase());
  const uniqueNames = new Set(normalizedNames);

  if (uniqueNames.size !== labels.length) {
    throw new Error('Arm labels must be unique');
  }

  return labels;
}

function validateBlockSize(blockSize, arms) {
  const size = toPositiveInteger(blockSize, 'Block size');

  if (size < arms) {
    throw new Error('Block size must be at least the number of arms');
  }

  if (size % arms !== 0) {
    throw new Error('Block size must be divisible by the number of arms');
  }

  if (size > 50) {
    throw new Error('Block size must be 50 or less for V1');
  }

  return size;
}

function validateCompleteFixedBlocks(n, blockSize) {
  if (n % blockSize !== 0) {
    throw new Error(`N must be divisible by block size for fixed block randomization; ${n} is not divisible by ${blockSize}`);
  }
}

function validateCompleteStratumBlocks(strata, blockSize) {
  const invalid = strata.find((stratum) => stratum.size % blockSize !== 0);

  if (invalid) {
    throw new Error(`Each stratum size must be divisible by block size for stratified fixed block randomization; ${invalid.id} has size ${invalid.size}, which is not divisible by ${blockSize}`);
  }
}

function validateBlockSizes(blockSizes, arms) {
  const sizes = String(blockSizes)
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => validateBlockSize(value, arms));

  if (sizes.length === 0) {
    throw new Error('At least one block size is required');
  }

  return [...new Set(sizes)].sort((a, b) => a - b);
}

function parseClusterToken(token, index) {
  const trimmed = token.trim();
  const namedMatch = trimmed.match(/^(.+?)\s*[:=]\s*(\d+)$/);

  if (namedMatch) {
    const id = namedMatch[1].trim();

    if (!id || id.length > 100) {
      throw new Error(`Cluster ${index + 1} must have a readable name`);
    }

    return {
      id,
      size: toPositiveInteger(namedMatch[2], id)
    };
  }

  return {
    id: `Cluster ${index + 1}`,
    size: toPositiveInteger(trimmed, `Cluster ${index + 1}`)
  };
}

function parseClusters(clusterInput) {
  return String(clusterInput || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .map(parseClusterToken);
}

function validateClusters(clusterInput, n) {
  const clusters = parseClusters(clusterInput);

  if (clusters.length === 0) {
    throw new Error('At least one cluster size is required');
  }

  const total = clusters.reduce((sum, cluster) => sum + cluster.size, 0);

  if (total !== n) {
    throw new Error(`Cluster sizes must sum to N (${n}); current total is ${total}`);
  }

  return clusters;
}

function parseStrata(strataInput) {
  return String(strataInput || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .map((token, index) => {
      const stratum = parseClusterToken(token, index);
      return {
        ...stratum,
        id: stratum.id.replace(/^Cluster /, 'Stratum ')
      };
    });
}

function validateStrata(strataInput, n) {
  const strata = parseStrata(strataInput);

  if (strata.length === 0) {
    throw new Error('At least one stratum size is required');
  }

  const total = strata.reduce((sum, stratum) => sum + stratum.size, 0);

  if (total !== n) {
    throw new Error(`Stratum sizes must sum to N (${n}); current total is ${total}`);
  }

  const names = strata.map((stratum) => stratum.id.toLowerCase());
  if (new Set(names).size !== names.length) {
    throw new Error('Stratum names must be unique');
  }

  return strata;
}

function getSmallStratumWarnings(strata, arms, label = 'Stratum') {
  return strata
    .filter((stratum) => stratum.size < arms)
    .map((stratum) => `${label} ${stratum.id} has fewer participants (${stratum.size}) than arms (${arms}); within-stratum balance is not possible.`);
}

function parseClusterStrata(clusterStrataInput) {
  return String(clusterStrataInput || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .map((token, index) => {
      const parts = token.split('|');

      if (parts.length !== 2) {
        throw new Error(`Cluster-stratified item ${index + 1} must use Stratum|Cluster:size`);
      }

      const stratum = parts[0].trim();
      const cluster = parseClusterToken(parts[1].trim(), index);

      if (!stratum || stratum.length > 100) {
        throw new Error(`Cluster-stratified item ${index + 1} must have a readable stratum name`);
      }

      return {
        ...cluster,
        stratum
      };
    });
}

function validateClusterStrata(clusterStrataInput, n, arms) {
  const clusters = parseClusterStrata(clusterStrataInput);

  if (clusters.length === 0) {
    throw new Error('At least one cluster-stratified item is required');
  }

  const total = clusters.reduce((sum, cluster) => sum + cluster.size, 0);

  if (total !== n) {
    throw new Error(`Cluster-stratified sizes must sum to N (${n}); current total is ${total}`);
  }

  const clusterNames = clusters.map((cluster) => `${cluster.stratum.toLowerCase()}|${cluster.id.toLowerCase()}`);
  if (new Set(clusterNames).size !== clusterNames.length) {
    throw new Error('Cluster names must be unique within each stratum');
  }

  const byStratum = new Map();
  clusters.forEach((cluster) => {
    const items = byStratum.get(cluster.stratum) || [];
    items.push(cluster);
    byStratum.set(cluster.stratum, items);
  });

  const warnings = [];
  byStratum.forEach((items, stratum) => {
    if (items.length < arms) {
      warnings.push(`Stratum ${stratum} has fewer clusters (${items.length}) than arms (${arms}); cluster-level balance may be limited.`);
    }
  });

  return {
    clusters,
    strata: Array.from(byStratum.entries()).map(([stratum, items]) => ({
      id: stratum,
      clusters: items,
      size: items.reduce((sum, cluster) => sum + cluster.size, 0)
    })),
    warnings
  };
}

module.exports = {
  getSmallStratumWarnings,
  normalizeArmLabels,
  parseClusters,
  parseClusterStrata,
  parseStrata,
  validateBlockSize,
  validateBlockSizes,
  validateClusterStrata,
  validateCompleteFixedBlocks,
  validateCompleteStratumBlocks,
  validateClusters,
  validateCoreParams,
  validateStrata
};
